import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Save, Sparkles, Check, X } from 'lucide-react';
import { Alert, Badge, Button, Loader, Select, Switch, Tabs } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { useChatDock } from '@/lib/ChatDockContext';
import Can from '@rbac/Can';
import { writeActionFor } from '@rbac/writeAction';
import RouteEditor from '@/components/mockapi/RouteEditor';
import TryItPanel from '@/components/mockapi/TryItPanel';
import RequestLogPanel from '@/components/mockapi/RequestLogPanel';
import EnvSettingsPanel from '@/components/mockapi/EnvSettingsPanel';
import { emptySpec, scenariosIn } from '@/lib/mockApiSpec';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * The mock environment workbench: edit the route/case spec, flip the active scenario,
 * try a request, read the request log, and configure proxy/record.
 *
 * Two dock integrations, both copied from the workflow builder because they solved the
 * same problems there:
 *  - `registerScreenContext` publishes the CURRENT (including unsaved) spec, so Mock API
 *    Builder revises what the user is looking at rather than the saved row. The agent's
 *    prompt is told to prefer this over get_mock_environment for exactly that reason.
 *  - a `propose_mock_spec` result arrives as a proposal and lands as UNSAVED editor
 *    state behind an Apply button. The human review is the approval, which is why that
 *    tool is `auto` and needs no gate.
 */
const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

/** What the save did to the tool catalog, so publishing a callable route is visible
 *  rather than a silent side effect of saving a spec. */
function toolNotice(result) {
  if (!result) return '';
  const parts = [];
  if (result.inserted?.length) parts.push(`published ${result.inserted.length} new tool(s)`);
  if (result.updated?.length) parts.push(`updated ${result.updated.length}`);
  if (result.disabled?.length) parts.push(`disabled ${result.disabled.length} whose route is gone`);
  return parts.length ? `Agents: ${parts.join(', ')}.` : '';
}

function saveLabel(saving, dirty) {
  if (saving) return 'Saving…';
  return dirty ? 'Save spec' : 'Saved';
}

function ProposalBanner({ proposal, onApply, onDismiss }) {
  const out = proposal && proposal.output;
  if (!out || !out.ok || !out.proposal) return null;
  const routes = (out.proposal.routes || []).length;
  return (
    <Alert variant="info" title="Mock API Builder has a draft">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">
          {out.summary || `${routes} route${routes === 1 ? '' : 's'} proposed.`} Nothing is saved until you review and Save.
        </span>
        <span className="flex-1" />
        <Button size="sm" onClick={() => onApply(out.proposal)}><Check size={13} /> Apply to editor</Button>
        <Button size="sm" variant="secondary" onClick={onDismiss}><X size={13} /> Dismiss</Button>
      </div>
    </Alert>
  );
}

/** The scenario switch and the on/off toggle — the two controls that change what every
 *  route answers, so they sit above the tabs rather than inside Settings. */
function ScenarioBar({ env, id, scenarios, setEnv, setErr, fetchApi }) {
  return (
      <Can module="mockapi" action={writeActionFor('mockapi')}>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--app-border)] px-3 py-2">
          <div className="w-56">
            <Select
              label="Active scenario"
              options={scenarios.map((s) => ({ label: s, value: s }))}
              value={env.active_scenario || 'default'}
              onChange={(v) => fetchApi('mockScenario', { id, scenario: v })
                .then(() => setEnv((e) => ({ ...e, active_scenario: v })))
                .catch((ex) => setErr(errText(ex)))}
            />
          </div>
          <div className="text-xs text-secondary-500 flex-1 min-w-[16rem]">
            Switches every route at once. Cases tagged <code className="font-mono">default</code> are always
            eligible; a case tagged with the active scenario overrides them.
          </div>
          <Switch
            label="Active"
            checked={!!env.is_active}
            onChange={(v) => fetchApi('mockEnvironmentUpdate', { id, isActive: !!v })
              .then((updated) => setEnv(updated))
              .catch((ex) => setErr(errText(ex)))}
          />
        </div>
      </Can>
  );
}

/**
 * Publish the CURRENT spec — unsaved edits included — to the chat dock.
 *
 * Registered as a getter, not a value: the dock reads it once at send time, so editing
 * a response body does not re-render the dock and its whole message list on every
 * keystroke. Mock API Builder's prompt is told to prefer this over get_mock_environment
 * precisely because this is the unsaved truth and that tool returns the saved row.
 */
function useScreenContext({ registerScreenContext, env, spec }) {
  const specRef = useRef(spec);
  specRef.current = spec;
  const envRef = useRef(env);
  envRef.current = env;

  useEffect(() => {
    if (!registerScreenContext) return undefined;
    return registerScreenContext(() => {
      const current = envRef.current;
      if (!current) return null;
      return [
        `The user is editing the mock API environment "${current.name}" (key: ${current.key}).`,
        `Mode: ${current.mode}. Active scenario: ${current.active_scenario}. Active: ${current.is_active ? 'yes' : 'no'}.`,
        'Its CURRENT spec, including unsaved edits, is:',
        JSON.stringify(specRef.current),
      ].join('\n');
    });
  }, [registerScreenContext]);
}

export default function MockApiDetail() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();
  const dock = useChatDock() || {};
  const { registerScreenContext, openDockWithAgent, proposal, clearProposal } = dock;

  const [env, setEnv] = useState(null);
  const [spec, setSpec] = useState(emptySpec());
  const [tools, setTools] = useState([]);
  const [savedSpec, setSavedSpec] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(spec) !== savedSpec;

  const load = useCallback(() => {
    setLoading(true);
    return fetchApi('mockEnvironment', id)
      .then((res) => {
        setEnv(res.environment);
        setSpec(res.spec || emptySpec());
        setSavedSpec(JSON.stringify(res.spec || emptySpec()));
        setTools(res.tools || []);
      })
      .catch((ex) => setErr(errText(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useScreenContext({ registerScreenContext, env, spec });

  const applyProposal = (proposed) => {
    // The proposal comes back in the normalized server shape, which is the same shape
    // the editor holds — so this is an assignment, not a translation.
    setSpec({ routes: proposed.routes || [] });
    if (clearProposal) clearProposal();
    setNotice('Draft applied to the editor. Review it, then Save.');
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    setNotice(null);
    try {
      const res = await fetchApi('mockSpecSave', { id, spec });
      setSavedSpec(JSON.stringify(spec));
      setNotice([
        `Saved ${res.routeCount} route(s), ${res.caseCount} case(s).`,
        toolNotice(res.tools),
        ...(res.warnings || []),
      ].filter(Boolean).join(' '));
      await load();
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  const scenarios = useMemo(() => {
    const fromSpec = scenariosIn(spec);
    const active = env?.active_scenario;
    return active && !fromSpec.includes(active) ? [...fromSpec, active] : fromSpec;
  }, [spec, env]);

  if (loading) return <PageShell><Loader /></PageShell>;
  if (!env) return <PageShell><Alert variant="error" title="Not found">{err || 'That mock environment does not exist.'}</Alert></PageShell>;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        backTo="/app-mock-environment"
        backLabel="Mock API"
        title={env.name}
        subtitle={<span className="font-mono text-xs">{env.key}</span>}
        meta={!env.is_active && <Badge variant="secondary" size="sm">inactive — answers 404 to everything</Badge>}
        actions={(
          <>
            {openDockWithAgent && (
              <Button size="sm" variant="secondary" onClick={() => openDockWithAgent('mock_api_builder')}>
                <Sparkles size={13} /> Ask Mock API Builder
              </Button>
            )}
            <Can module="mockapi" action={writeActionFor('mockapi')}>
              <Button size="sm" onClick={save} disabled={saving || !dirty}>
                <Save size={13} /> {saveLabel(saving, dirty)}
              </Button>
            </Can>
          </>
        )}
      />

      {env.mode !== 'mock' && (
        <Alert variant="warning" title={`This mock is in ${env.mode} mode`}>
          Its routes still bind as tools, but every one of them is now <b>gated</b> — a call that
          the spec does not match reaches {env.upstream_url || 'the real upstream'} with real
          credentials, so it needs a human to approve it and is denied outright on unattended
          surfaces (scheduled tasks, the REST API, MCP). Switch back to mock mode to let agents
          call it freely.
        </Alert>
      )}
      {err && <Alert variant="error" title="Something went wrong">{err}</Alert>}
      {notice && <Alert variant="success" title="Done">{notice}</Alert>}
      <ProposalBanner proposal={proposal} onApply={applyProposal} onDismiss={clearProposal} />

      <ScenarioBar env={env} id={id} scenarios={scenarios} setEnv={setEnv} setErr={setErr} fetchApi={fetchApi} />

      <Tabs defaultIndex={0} variant="line" size="sm">
        <Tabs.List>
          <Tabs.Tab index={0}>Routes{dirty ? ' •' : ''}</Tabs.Tab>
          <Tabs.Tab index={1}>Try it</Tabs.Tab>
          <Tabs.Tab index={2}>Requests</Tabs.Tab>
          <Tabs.Tab index={3}>Settings</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panels className="pt-3">
          <Tabs.Panel index={0}>
            <RouteEditor spec={spec} onChange={setSpec} tools={tools} />
          </Tabs.Panel>
          <Tabs.Panel index={1}>
            <TryItPanel environmentId={id} dirty={dirty} />
          </Tabs.Panel>
          <Tabs.Panel index={2}>
            <RequestLogPanel environmentId={id} />
          </Tabs.Panel>
          <Tabs.Panel index={3}>
            <EnvSettingsPanel environment={env} onChanged={setEnv} />
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs>
    </PageShell>
  );
}
